import http from '@/shared/http';
import { TokenService } from '@/shared/http/token';
import { INSIGHTS_ASK_TIMEOUT_MS, INSIGHTS_ASK_TIMEOUT_MESSAGE } from './constants/insightsClient.js';

const API_BASE = '/api';

export function normalizeClarificationPayload(payload = {}) {
  const clarification = payload?.clarification || {};
  return {
    success: false,
    outcome: 'clarification',
    runId: payload?.runId || null,
    sessionId: payload?.sessionId || null,
    missingScope: payload?.missingScope || [],
    scope: payload?.scope || null,
    reasoning: payload?.reasoning || null,
    clarification: {
      mode: clarification.mode || payload?.mode || 'single',
      prompt: clarification.prompt || payload?.prompt || null,
      options: clarification.options || payload?.options || [],
    },
  };
}

export function normalizeResultPayload(payload = {}, overrides = {}) {
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  return {
    ...payload,
    ...overrides,
    rows,
    outcome: overrides.outcome || payload?.outcome || 'success',
  };
}

export function buildCachedResultPayload(cachedResult, overrides = {}) {
  if (!cachedResult?.rows?.length) {
    return null;
  }
  return normalizeResultPayload({
    success: true,
    rows: cachedResult.rows,
    presentation: cachedResult.presentation || cachedResult.presentationMeta || null,
    userMessage: cachedResult.userMessage || cachedResult.summary || null,
    answerMeta: cachedResult.answerMeta || null,
    reasoning: cachedResult.reasoning || null,
    metricId: cachedResult.metricId || null,
    runId: cachedResult.runId || null,
    outcome: 'success',
    tookMs: 0,
  }, overrides);
}

class InsightsService {
  ask(question, timeWindow, limit, options = {}) {
    return http.post('/insights/ask', {
      question,
      timeWindow,
      limit,
      sessionId: options.sessionId || null,
      scopePatch: options.scopePatch || null,
    });
  }

  /**
   * Stream ask progress via SSE (POST).
   * @param {string} question
   * @param {object} [options]
   */
  async askStream(question, options = {}) {
    const {
      timeWindow = null,
      limit = null,
      sessionId = null,
      scopePatch = null,
      onStep,
      signal,
    } = options;

    const token = TokenService.getToken();
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const timeoutController = new AbortController();
    const onExternalAbort = () => timeoutController.abort();
    if (signal) {
      if (signal.aborted) {
        timeoutController.abort();
      } else {
        signal.addEventListener('abort', onExternalAbort, { once: true });
      }
    }

    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      timeoutController.abort();
    }, INSIGHTS_ASK_TIMEOUT_MS);

    let response;
    try {
      response = await fetch(`${API_BASE}/insights/ask/stream`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          question,
          timeWindow,
          limit,
          sessionId,
          scopePatch,
        }),
        signal: timeoutController.signal,
      });
    } catch (fetchErr) {
      clearTimeout(timeoutId);
      if (signal) signal.removeEventListener('abort', onExternalAbort);
      if (timedOut) {
        const err = new Error(INSIGHTS_ASK_TIMEOUT_MESSAGE);
        err.code = 'INSIGHTS_ASK_TIMEOUT';
        throw err;
      }
      throw fetchErr;
    }

    if (response.status === 410) {
      clearTimeout(timeoutId);
      if (signal) signal.removeEventListener('abort', onExternalAbort);
      const err = new Error('Session expired. Please start fresh.');
      err.response = { status: 410, data: { outcome: 'expired' } };
      throw err;
    }

    if (!response.ok) {
      clearTimeout(timeoutId);
      if (signal) signal.removeEventListener('abort', onExternalAbort);
      let errorBody = {};
      try {
        errorBody = await response.json();
      } catch {
        // non-JSON error body
      }
      const err = new Error(errorBody.userMessage || errorBody.error || 'Stream request failed');
      err.response = { data: errorBody, status: response.status };
      throw err;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      clearTimeout(timeoutId);
      if (signal) signal.removeEventListener('abort', onExternalAbort);
      throw new Error('Streaming not supported by this browser');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult = null;
    let streamError = null;
    let clarificationPayload = null;

    const processEventBlock = (block) => {
      const lines = block.split('\n');
      let eventType = 'message';
      let dataLine = '';
      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          // Per SSE spec, multiple data: lines are joined with \n
          dataLine += (dataLine ? '\n' : '') + line.slice(5);
        }
      }
      if (!dataLine) return;

      let payload;
      try {
        payload = JSON.parse(dataLine);
      } catch {
        return;
      }

      if (eventType === 'step' && typeof onStep === 'function') {
        onStep(payload);
      } else if (eventType === 'result' || eventType === 'advisor') {
        finalResult = payload;
      } else if (eventType === 'clarification_required') {
        clarificationPayload = payload;
      } else if (eventType === 'error') {
        streamError = payload;
      }
    };

    try {
      let readResult = await reader.read();
      while (!readResult.done) {
        buffer += decoder.decode(readResult.value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          if (part.trim()) {
            processEventBlock(part.trim());
          }
        }
        readResult = await reader.read();
      }

      if (buffer.trim()) {
        processEventBlock(buffer.trim());
      }
    } finally {
      // Timeout stays active throughout the stream read loop — clear it now
      clearTimeout(timeoutId);
      if (signal) signal.removeEventListener('abort', onExternalAbort);
    }

    if (clarificationPayload) {
      return normalizeClarificationPayload(clarificationPayload);
    }

    if (streamError) {
      const err = new Error(streamError.userMessage || streamError.error || 'Query failed');
      err.response = { data: streamError };
      throw err;
    }

    if (!finalResult) {
      throw new Error('Stream ended without a result');
    }

    return finalResult;
  }

  closeSession(sessionId) {
    return http.post('/insights/session/close', { sessionId });
  }

  getCatalogStatus() {
    return http.get('/insights/catalog/status');
  }
}

export default new InsightsService();
